/**
 * Frontend measurement-points calculation — input/output contracts.
 *
 * See docs/frontend-measurement-calculation.md. This module is the portable
 * equivalent of taman-ml's measurement step: given luminaires (with their IES
 * candela distribution), surface grid points (with normals) and norm targets,
 * it produces the SAME `MeasurementData[]` shape the workspace already renders.
 *
 * Pure + side-effect free so it can run in a Web Worker
 * and be unit-tested against an ml `measurement.json` golden file.
 */
import type { ExpectedValue, MeasurementData } from '../../store/reducers/workspace.reducer';
import type { IesCandelaGrid } from '../../types/lampWireframes';

/** Per-user feature flag key (mirrors the backend Flyway-seeded flag). */
export const FRONTEND_MEASUREMENT_CALCULATION_FLAG = 'frontend-measurement-calculation';

export type Vec3 = [number, number, number];

/** Photometric quantities, in the exact array order used by `measurements`: L, E, Ev, Esc, Ehs. */
export type MeasurementField = 'L' | 'E' | 'Ev' | 'Esc' | 'Ehs';

/** Value written into `measurements[i][field]` when a quantity is not computed. */
export const NOT_COMPUTED = -1;

export interface CalcLuminaire {
  uuid: string;
  position: Vec3;
  /** Mounting height above the measured surface, in metres. */
  height: number;
  /** Optional euler rotation (radians) for aiming. TODO(phase-2): apply to IES sampling. */
  rotation?: Vec3;
  /**
   * Photometric distribution — the same `IesCandelaGrid` the app already parses
   * from `.ies`/`.ldt` files (`src/util/ldc.ts`) and stores under `lampWireframes`.
   * `candela[azimuthIndex][elevationIndex]`, angles in radians, sorted ascending.
   */
  candela: IesCandelaGrid;
  /** Optional multiplier applied to sampled candela (e.g. lumen scaling). */
  lumenScale?: number;
}

export interface CalcPoint {
  /** World-space position on the surface. */
  position: Vec3;
  /** Unit surface normal. */
  normal: Vec3;
}

/**
 * World-space surface triangles (Z up). Plain typed arrays so the whole layer
 * is structured-cloneable / transferable into the worker, where the grid is
 * sampled (the heavy raycast stays off the main thread).
 */
export interface SurfaceGeometry {
  /** Flat triangle vertex positions `[x, y, z, ...]`. */
  positions: ArrayLike<number>;
  /** Optional triangle index; absent = non-indexed (3 verts per triangle). */
  index?: ArrayLike<number>;
}

export interface CalcLayer {
  uuid: string;
  name: string;
  normId: number;
  /** World-space surface; the worker samples the measurement grid from this. */
  geometry: SurfaceGeometry;
  /**
   * Pre-sampled measurement points (position + normal). When provided the engine
   * uses these directly and skips top-down grid sampling of `geometry`. Measurement
   * planes generate these in their own frame — see utils/measurementPlane/sampler.
   */
  points?: CalcPoint[];
  /** Norm targets for this layer (drives `expectedValues`). */
  targets: ExpectedValue[];
  /** Which photometric quantities this layer requires. */
  fields: MeasurementField[];
  /**
   * Keep every sampled point even if no luminaire reaches it (occluded / out of range),
   * reporting 0 for those. ml drops such points, but user-drawn measurement surfaces should
   * show the full grid — the shadowed spots read 0 lx so the occlusion is visible.
   */
  keepAllPoints?: boolean;
}

export interface CalcConfig {
  /** Maintenance factor f_M (e.g. 0.9). */
  maintenanceFactor: number;
  /** Measurement-grid spacing in metres (ml `measurementPointsGridDistance`). Default 0.5. */
  gridSpacingM?: number;
  /** Points within this distance (m) of the sampled-region edge are ignored (ml uses ~1.5). */
  boundaryThresholdM?: number;
  /**
   * Max apex angle (deg) from straight down. 0° is nadir, 90° the horizon, 180° the full sphere
   * (the angle test is skipped). Default 77.5.
   */
  maxApexAngleDegrees?: number;
  /**
   * Occlusion test (ml `_filter_visible_points`): drop a (light, point) pair when
   * a surface blocks the segment between them, and drop points no light can see.
   * Default true — matches ml, which reports fewer points than a pure apex gate.
   */
  occlusion?: boolean;
  /** Observer height for d_lkp, in metres. Default 1.5. */
  observerHeightM?: number;
  /** Percentile trimmed off each tail for MIN/MAX aggregates. Default 2. */
  minMaxTrimPercentile?: number;
  /**
   * Mark "boundary" points (near the sampled-region edge) as ignored, as ml does for
   * road grids. Default true. Set false for user-drawn measurement surfaces, where the
   * user explicitly drew the area — every point should count, no edge trimming (otherwise
   * a small surface has all points ignored and its values can't match the model grid).
   */
  boundaryTrim?: boolean;
  /**
   * In multi-light scenes, mark points as ignored when visible from at most one luminaire
   * (0 or 1), so aggregates favour overlap regions (ml default). With `keepAllPoints`,
   * zero-visibility points are still emitted in the grid but excluded from statistics via
   * this mask. Set false for user-drawn measurement surfaces where every grid point should count.
   */
  singleLampTrim?: boolean;
  /**
   * Compute E with the general planar formula (I·f·cosθ/d²) using the point normal
   * instead of the road-specific cos³ε/h². Needed for oriented (façade) measurement
   * planes; numerically equivalent to the road E on horizontal surfaces.
   */
  planarIlluminance?: boolean;
}

export interface CalcInput {
  luminaires: CalcLuminaire[];
  layers: CalcLayer[];
  config: CalcConfig;
  /**
   * Extra occluder geometry not measured itself (e.g. the imported model when only
   * measurement-plane layers are computed). Added to the occlusion scene so buildings
   * still cast shadows onto the planes.
   */
  occluders?: SurfaceGeometry[];
}

/** Identical shape to what the ml path produces and the workspace renders. */
export type CalcOutput = MeasurementData[];
